import {
  createStyles,
  Drawer as MuiDrawer,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  BANNER_HEIGHT,
  COMPANY_SWITCHER_WIDTH,
  DRAWER_WIDTH,
  ELECTRON_TITLE_BAR_HEIGHT,
} from 'src/constants/uiConsts';
import { SidebarTheme, SidebarThemeContext } from 'src/context';
import { DesktopCompanySwitcher } from 'src/features/companies/CompanySwitcher';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { isElectron } from 'src/hooks/useElectron';
import { RootState } from 'src/store';

interface DrawerStyleProps {
  showBanner: boolean;
  drawerTheme: SidebarTheme;
}

const useStyles = makeStyles<
  Theme,
  { showBanner: boolean; drawerTheme: SidebarTheme }
>(() =>
  createStyles({
    mobileDrawer: {
      backgroundColor: (props) => props.drawerTheme.sidebarBackgroundColor,
      width: DRAWER_WIDTH,
      flexShrink: 0,
    },
    desktopDrawer: {
      boxSizing: 'border-box',
      backgroundColor: (props) => props.drawerTheme.sidebarBackgroundColor,
      width: DRAWER_WIDTH,
      marginTop: (props: DrawerStyleProps) =>
        props.showBanner ? `${BANNER_HEIGHT}px` : 0,
    },
  }),
);

export const Drawer = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const companySwitcherVisibility = useCompanySwitcherVisibility();
  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerTheme = React.useContext(SidebarThemeContext);
  const classes = useStyles({
    showBanner: Boolean(bannerOptions),
    drawerTheme,
  });

  React.useEffect(() => {
    function handleResize() {
      if (!isMobile && open) {
        onClose();
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, open, onClose]);

  return (
    <div className="z-modal">
      <div className={isMobile ? 'block' : 'hidden'}>
        <MuiDrawer
          variant="temporary"
          classes={{
            paper: classes.mobileDrawer,
          }}
          anchor="left"
          onClose={onClose}
          open={open}
        >
          {children}
        </MuiDrawer>
      </div>
      <div className={isMobile ? 'hidden' : 'block'}>
        {companySwitcherVisibility.visibility === 'desktop' && (
          <div
            style={{
              backgroundColor: drawerTheme.sidebarBackgroundColor,
              width: COMPANY_SWITCHER_WIDTH,
              ...(isElectron() && { top: ELECTRON_TITLE_BAR_HEIGHT }),
            }}
            className="box-border fixed left-0 bottom-0 border-0 border-solid border-r-[1px] border-r-[rgba(0,0,0,0.1)]"
          >
            <DesktopCompanySwitcher theme={drawerTheme} />
          </div>
        )}
        <div
          style={{
            // This needs to be the constant rather than the variable.
            width: DRAWER_WIDTH,
            left:
              companySwitcherVisibility.visibility === 'desktop'
                ? COMPANY_SWITCHER_WIDTH
                : 0,
            ...(isElectron() && { top: ELECTRON_TITLE_BAR_HEIGHT }),
          }}
          className={[
            classes.desktopDrawer,
            'box-border',
            'flex',
            'flex-col',
            'fixed',
            'bottom-0',
            'overflow-y-auto',
            'shadow-sm',
            'border-0',
          ].join(' ')}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
